import type { LogLevel } from "whole-shared";
import { logger } from "whole-shared";

import { EventEmitter } from "../../config.js";
import type { Model } from "../Model";
import type { ClientAbility, ClientTwistCard } from "../rules/index.js";
import type { Unit } from "../Unit";

export type Choice = [name: string, onClick: () => void, disabled?: boolean];

export interface TooltipItem {
  twistCard?: ClientTwistCard;
  ability?: ClientAbility;
  unit?: { unit: Unit; model: Model };
  position?: { x: number; y: number };
}

type HudControllerEvents = {
  armiesSet: [setting: boolean];
  armiesShow: [show: boolean];
  choices: [choices: Choice[]];
  commanders: [commander1: string, commander2: string];
  counter: [counter: number];
  counter_max: [counter_max: number];
  counter_visible: [counter_visible: boolean];
  description: [description: string];
  devWindow: [show: boolean];
  diceReset: [];
  enable: [enabled: boolean];
  header: [header: string];
  log: [level: LogLevel, ...args: unknown[]];
  logAttached: [attached: boolean];
  phase: [phase: string];
  reset: [];
  roll: [content: string];
  round: [round: number];
  subheader: [subheader: string];
  tooltip: [item?: TooltipItem, onEnter?: () => void];
  turn: [turn: number];
  twistCard: [card?: ClientTwistCard];
  underdog: [underdog: number];
  unit_tooltip: [item?: TooltipItem];
  victoryPoints: [points: number[]];
  visibility: [visible: boolean];
};

export class HudController extends EventEmitter<HudControllerEvents> {
  private choices = new Map<string, Choice>();

  /* Choices */

  addChoice(...choice: Choice) {
    const [name] = choice;

    if(this.choices.has(name)) logger.warn(`Choice ${name} already exists`);

    this.choices.set(name, choice);
    this.emitChoices();

    return () => this.removeChoice(name);
  }

  disableChoice(name: string) {
    const choice = this.choices.get(name);

    if(choice) {
      choice[2] = true;
      this.emitChoices();
    } else logger.warn(`Choice ${name} does not exist`);
  }

  enableChoice(name: string) {
    const choice = this.choices.get(name);

    if(choice) {
      choice[2] = false;
      this.emitChoices();
    } else logger.warn(`Choice ${name} does not exist`);
  }

  removeChoice(name: string) {
    if(this.choices.has(name)) {
      this.choices.delete(name);
      this.emitChoices();
    } else logger.warn(`Choice ${name} does not exist`);
  }

  updateChoice(...choice: Choice) {
    const [name] = choice;

    if(! this.choices.has(name)) logger.warn(`Choice ${name} does not exist`);

    this.choices.set(name, choice);
    this.emitChoices();
  }

  private emitChoices() {
    this.emit("choices", [...this.choices.values()]);
  }

  /* Wrappers */

  armiesSet(setting: boolean) {
    this.emit("armiesSet", setting);
  }

  armiesShow(show: boolean) {
    this.emit("armiesShow", show);
  }

  commanders(commander1: string, commander2: string) {
    this.emit("commanders", commander1, commander2);
  }

  description(text: string) {
    this.emit("description", text);
  }

  devWindow(show: boolean) {
    this.emit("devWindow", show);
  }

  enable(enabled: boolean) {
    this.emit("enable", enabled);
  }

  setVisibility(visible: boolean) {
    this.emit("visibility", visible);
  }

  setHeader(header: string) {
    this.emit("header", header);
  }

  setSubheader(subheader: string) {
    this.emit("subheader", subheader);
  }

  setDescription(text: string) {
    this.emit("description", text);
  }

  setCounterVisible(counter_visible: boolean) {
    this.emit("counter_visible", counter_visible);
  }

  setCounter(counter: number) {
    this.emit("counter", counter);
  }

  setCounterMax(counter_max: number) {
    this.emit("counter_max", counter_max);
  }

  phase(phase: string) {
    this.emit("phase", phase);
  }

  reset() {
    this.choices.clear();
    this.emit("reset");
  }

  round(round: number) {
    this.emit("round", round);
  }

  turn(turn: number) {
    this.emit("turn", turn);
  }

  twistCard(card?: ClientTwistCard) {
    this.emit("twistCard", card);
  }

  underdog(underdog: number) {
    this.emit("underdog", underdog);
  }

  victoryPoints(points: number[]) {
    this.emit("victoryPoints", points);
  }

  clear() {
    logger.warn(new Error("Forbidden hud.clear, use hud.reset instead"));
    this.reset();
  }
}

export const hud = new HudController();
